/**
 * MenuPage — Super-app style full-page menu (mobile /menu route)
 *
 * Layout inspired by Grab/LINE/Shopee account pages:
 * - Profile card at top
 * - App grid
 * - Grouped settings/utility sections
 */

import { Link } from "react-router-dom";
import {
  Gear,
  SignOut,
  Question,
  Bell,
  UserCircle,
  CaretRight,
  Globe,
} from "@phosphor-icons/react";
import type { IconProps } from "@phosphor-icons/react";
import type { ComponentType } from "react";
import { cn } from "@/lib/utils";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { useAuthStore } from "@/lib/store/authStore";
import { useAppAccess } from "@/hooks/useAppAccess";
import {
  getAppItems,
} from "@/lib/config/navigationConfig";
import { ErpSidebarContent } from "@/components/layout/ErpSidebarContent";

// ── App PNG icons (same map as QuickActions) ──

const APP_ICON_MAP: Record<string, string> = {
  "ธุรกิจ": "/app-icons/Business.png",
  "CRM": "/app-icons/CRM.png",
  "กล่องข้อความ": "/app-icons/กล่องข้อความ.png",
  "แชททีม": "/app-icons/แชททีม.png",
  "รายงาน": "/app-icons/รายงาน.png",
  "สร้างเว็บ": "/app-icons/สร้างเว็บ.png",
  "อีเมล": "/app-icons/อีเมล.png",
  "โน้ต": "/app-icons/โน้ต.png",
  "POS": "/app-icons/POS.png",
  "การจัดส่ง": "/app-icons/จัดส่ง.png",
  "แอป": "/app-icons/แอป.png",
  "บล็อก": "/app-icons/Blog.png",
};

const DESCRIPTIVE_LABELS: Record<string, string> = {
  "ธุรกิจ": "จัดการธุรกิจ",
  "CRM": "CRM",
  "กล่องข้อความ": "กล่องข้อความ",
  "รายงาน": "รายงาน",
  "สร้างเว็บ": "สร้างเว็บ",
  "อีเมล": "อีเมล",
  "โน้ต": "โน้ต",
  "POS": "POS",
  "การจัดส่ง": "การจัดส่ง",
};

/** Strip tenant ID suffix from company name */
const TENANT_SUFFIX = /\s-\s[a-z0-9]{10}$/;

// ── Menu sections ──

interface MenuRow {
  label: string;
  href?: string;
  icon: ComponentType<IconProps>;
  iconBg: string;
  iconColor: string;
  onClick?: () => void;
}

export function MenuPage() {
  const { data: user } = useCurrentUser();
  const rawCompany = useAuthStore((s) => s.tenantCompany);
  const company = rawCompany ? rawCompany.replace(TENANT_SUFFIX, "") : null;
  const storefrontUrl = useAuthStore((s) => s.storefrontUrl);
  const logout = useAuthStore((s) => s.logout);
  const { hasAccess, isInstalled } = useAppAccess();

  // App grid — same source as QuickActions
  const allApps = getAppItems();
  const apps = allApps.filter(
    (app) => !app.appAccessKey || hasAccess(app.appAccessKey)
  ).filter(
    // Opt-in only (Sheets): every other app keeps the rule above unchanged.
    (app) => !app.installGatedEverywhere || !app.appRegistryKey || isInstalled(app.appRegistryKey)
  );

  const userName = user?.first_name
    ? `${user.first_name}${user.last_name ? ` ${user.last_name}` : ""}`
    : user?.full_name || user?.name || "";

  const sections: { title?: string; rows: MenuRow[] }[] = [
    {
      title: "ทั่วไป",
      rows: [
        { label: "การแจ้งเตือน", href: "/settings", icon: Bell, iconBg: "bg-red-500/10", iconColor: "text-red-500" },
        { label: "ตั้งค่าบัญชี", href: "/settings", icon: Gear, iconBg: "bg-gray-500/10", iconColor: "text-gray-600" },
        { label: "ภาษา", href: "/settings", icon: Globe, iconBg: "bg-blue-500/10", iconColor: "text-blue-500" },
      ],
    },
    {
      title: "ช่วยเหลือ",
      rows: [
        { label: "ศูนย์ช่วยเหลือ", href: "/settings", icon: Question, iconBg: "bg-emerald-500/10", iconColor: "text-emerald-500" },
      ],
    },
    {
      rows: [
        { label: "ออกจากระบบ", icon: SignOut, iconBg: "bg-red-500/10", iconColor: "text-red-500", onClick: logout },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-background pb-4">
      {/* ── Profile Card ── */}
      <div className="bg-white dark:bg-gray-950 px-5 pt-4 pb-5">
        <Link to="/settings" className="flex items-center gap-3.5">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            {user?.user_image ? (
              <img src={user.user_image} alt="" className="w-14 h-14 rounded-full object-cover" />
            ) : (
              <UserCircle size={36} weight="fill" className="text-primary" />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-base font-semibold text-foreground truncate">{userName || "ผู้ใช้งาน"}</p>
            {company && (
              <p className="text-xs text-muted-foreground truncate mt-0.5">{company}</p>
            )}
            {storefrontUrl && (
              <p className="text-[11px] text-muted-foreground/60 truncate mt-0.5">
                {storefrontUrl.replace(/^https?:\/\//, "")}
              </p>
            )}
          </div>
          <CaretRight size={18} className="text-gray-400 shrink-0" />
        </Link>
      </div>

      {/* ── App Grid ── */}
      <div className="bg-white dark:bg-gray-950 mt-2 px-5 py-5">
        <h3 className="text-sm font-semibold text-foreground mb-4">แอปทั้งหมด</h3>
        <div className="grid grid-cols-4 gap-y-5 gap-x-3">
          {apps.map((app) => {
            const iconSrc = APP_ICON_MAP[app.name];
            const href = app.href ?? app.subMenu?.[0]?.items?.[0]?.href ?? "/";
            return (
              <Link
                key={app.name}
                to={href}
                className="group flex flex-col items-center"
              >
                <div
                  className={cn(
                    "transition-transform duration-150",
                    "group-hover:scale-105 group-active:scale-90",
                  )}
                >
                  {iconSrc ? (
                    <img src={iconSrc} alt={app.name} className="w-11 h-11 object-contain" loading="lazy" />
                  ) : (
                    <div className="w-11 h-11 flex items-center justify-center rounded-[12px] bg-gray-100 dark:bg-gray-800">
                      <app.icon className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
                    </div>
                  )}
                </div>
                <span className="mt-2 text-[11px] font-medium text-muted-foreground text-center leading-tight">
                  {DESCRIPTIVE_LABELS[app.name] ?? app.name}
                </span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* ── Navigation (same as sidebar) ── */}
      <div className="bg-white dark:bg-gray-950 mt-2">
        <ErpSidebarContent />
      </div>

      {/* ── Menu Sections ── */}
      {sections.map((section, i) => (
        <div key={i} className="bg-white dark:bg-gray-950 mt-2">
          {section.title && (
            <div className="px-5 pt-4 pb-1">
              <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{section.title}</h3>
            </div>
          )}
          {section.rows.map((row) => {
            const content = (
              <div className="flex items-center gap-3 px-5 py-3 active:bg-gray-50 dark:active:bg-gray-900 transition-colors">
                <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", row.iconBg)}>
                  <row.icon size={20} weight="fill" className={row.iconColor} />
                </div>
                <span className="flex-1 text-sm text-foreground">{row.label}</span>
                <CaretRight size={16} className="text-gray-300 shrink-0" />
              </div>
            );

            if (row.onClick) {
              return (
                <button key={row.label} type="button" onClick={row.onClick} className="w-full text-left">
                  {content}
                </button>
              );
            }

            return (
              <Link key={row.label} to={row.href ?? "/"}>
                {content}
              </Link>
            );
          })}
        </div>
      ))}
    </div>
  );
}
